import type { Product } from '@/domain/Product';
import type { PromotionCardDefinition } from '@/widgets/ProductList/config';

export type CollectionCard = CollectionPromoCard | CollectionProductCard;

export type CollectionProductCard = {
  cardId: Product['id'];
  type: 'product';
  product: Product;
  index: number;
};

export type CollectionPromoCard = {
  cardId: PromotionCardDefinition['promotionId'];
  type: 'promotion';
  promo: PromotionCardDefinition;
  index: number;
};

export function getAllCards({
  products,
  displayingCollection = false,
  promotionCards
}: {
  products: Product[];
  displayingCollection?: boolean;
  promotionCards: PromotionCardDefinition[];
}): CollectionCard[] {
  const { before: beforeAllCards, after: afterAllCards } = displayingCollection
    ? getBeforeAndAfterPromotionCards(promotionCards)
    : {
        after: [],
        before: []
      };

  return products.flatMap((product, index) => {
    const isFirstItem = index === 0;
    const isLastItem = index >= products.length - 1;

    const productCard: CollectionCard = {
      type: 'product',
      product,
      index,
      cardId: product.id
    };

    const toPromoCard = (promo: PromotionCardDefinition) =>
      ({
        type: 'promotion',
        promo,
        cardId: promo.promotionId,
        index
      }) satisfies CollectionCard;

    let { before: beforePromotionCards, after: afterPromotionCards } =
      getBeforeAndAfterPromotionCards(promotionCards, product);

    if (isFirstItem) {
      beforePromotionCards = beforePromotionCards.concat(beforeAllCards);
    }
    if (isLastItem) {
      afterPromotionCards = afterPromotionCards.concat(afterAllCards);
    }

    return [
      ...beforePromotionCards.map(toPromoCard),
      productCard,
      ...afterPromotionCards.map(toPromoCard)
    ];
  });
}

/**
 * Returns promo cards for before and after a given product
 * If no product provided, returns promo cards before and after ALL products (no specific product assigned)
 */
export function getBeforeAndAfterPromotionCards(
  allCards: PromotionCardDefinition[],
  product?: Product
) {
  const { before = [], after = [] } = Object.groupBy(
    allCards.filter((card) =>
      product ? cardMatchesProductId(card, product) : !card.productId
    ),
    (card) => card.placement
  );

  return { before, after };
}

export function cardMatchesProductId(
  card: PromotionCardDefinition,
  { id, nessoft: { legacyId } }: Product
): boolean {
  const shortProductId = legacyId;
  if (card.productId !== undefined) {
    if (card.productId === id || card.productId === shortProductId) {
      return true;
    }
  }
  return false;
}
